import React, { useState, useRef, useEffect } from "react";
import { Info, X } from "lucide-react";
import { useHistory, useLocation } from "react-router-dom";
import WorkInProgress from "../../components/WorkInProgress.js";
import DiscardConfirmationModal from "../../components/DiscardConfirmationModal.js";
import AlertNotification from "../../components/AlertNotification";
import apiHelper from "../../apiHelper.js";

const portalRoot =
  typeof document !== "undefined"
    ? document.getElementById("portal-root")
    : null;

export default function BulkPricing() {
  const history = useHistory();
  const location = useLocation();
  const [showWip, setShowWip] = useState(false);
  const [discardModalOpen, setDiscardModalOpen] = useState(false);
  const initialSnapshotRef = useRef(null);
  const [basePriceErrors, setBasePriceErrors] = useState({});
  const [overrideErrors, setOverrideErrors] = useState({});
  const [editedPrices, setEditedPrices] = useState({});
  const [nextLocation, setNextLocation] = useState(null);
  const [discardSource, setDiscardSource] = useState(null);
  const breadcrumbs = ["Listing", "Platform Pricing", "Bulk Pricing"];
  const [rows, set_rows] = useState([
    { id: 1, platform: "", product_id: "", sku_id: "", platform_open: false },
  ]);
  const row_refs = useRef({});
  const allowNavigationRef = useRef(false);
  const [changeReason, setChangeReason] = useState("");
  const [changeReasonError, setChangeReasonError] = useState("");
  const changepriceRef = useRef(null);
  const [changepriceModalOpen, setChangepriceModalOpen] = useState(false);
  const [editSuccess, setEditSuccess] = useState(false);
  const save_ref2 = useRef(null);
  const [save_modal_open_2, set_save_modal_open_2] = useState(false);
  const [notification, setNotification] = useState(null);

  const closeChangePriceModal = () => {
    setChangepriceModalOpen(false);
    setChangeReason("");
    setChangeReasonError("");
  };

  const handle_save_confirm_2 = () => set_save_modal_open_2(false);
  useEffect(() => {
    function outside(e) {
      if (save_ref2.current && !save_ref2.current.contains(e.target))
        set_save_modal_open_2(false);
    }
    if (save_modal_open_2) document.addEventListener("mousedown", outside);
    return () => document.removeEventListener("mousedown", outside);
  }, [save_modal_open_2]);


  const handleNotificationClose = () => {
    setNotification(null);
  };

  // select stock dropdown state (kept)
  const select_stock_ref = useRef(null);
  const [select_stock_open, set_select_stock_open] = useState(false);

  const STOCK_OPTIONS = [
    {
      title: "Samsung S25 Plus",
      sku: "Mint, 12GB, 256GB",
      condition: "New",
      status: "Unactivated",
    },
    {
      title: "Samsung S25 Plus",
      sku: "Icyblue, 12GB, 256GB",
      condition: "New",
      status: "Unactivated",
    },
    {
      title: "Samsung S25 Plus",
      sku: "Navy, 12GB, 256GB",
      condition: "New",
      status: "Unactivated",
    },
    {
      title: "Samsung S25 Plus",
      sku: "Navy, 12GB, 256GB",
      condition: "Used",
      status: "Pristine",
    },
    {
      title: "Samsung S25 Plus",
      sku: "Navy, 12GB, 512GB",
      condition: "Used",
      status: "Pristine",
    },
    {
      title: "Samsung S25 Plus",
      sku: "Silver Shadow, 12GB, 256GB",
      condition: "New",
      status: "Unactivated",
    },
    {
      title: "Samsung S25 Plus",
      sku: "Blueblack, 12GB, 256GB",
      condition: "New",
      status: "Unactivated",
    },
    {
      title: "Apple iPhone 16",
      sku: "Starlight, 8GB, 256GB",
      condition: "New",
      status: "Activated",
    },
    {
      title: "Apple iPhone 16",
      sku: "Black, 8GB, 512GB",
      condition: "Used",
      status: "Refurbished",
    },
    {
      title: "Apple iPhone 15",
      sku: "Red, 6GB, 128GB",
      condition: "New",
      status: "Unactivated",
    },
    {
      title: "Apple iPad Air",
      sku: "Space Gray, 8GB, 256GB",
      condition: "New",
      status: "Unactivated",
    },
    {
      title: "Apple iPad Pro",
      sku: "Silver, 16GB, 1TB",
      condition: "Used",
      status: "Pristine",
    },
  ];

  useEffect(() => {
    const unblock = history.block((tx) => {
      // Allow navigation after confirm
      if (allowNavigationRef.current) {
        allowNavigationRef.current = false;
        return true;
      }

      // Open discard modal
      setDiscardSource("navigate");
      setNextLocation(tx);
      setDiscardModalOpen(true);

      return false;
    });

    return () => unblock();
  }, []);

  useEffect(() => {
    if (Object.keys(editedPrices).length === 0) return;
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [editedPrices]);

  useEffect(() => {
    function onDocClick(e) {
      if (
        select_stock_ref.current &&
        !select_stock_ref.current.contains(e.target)
      ) {
        set_select_stock_open(false);
      }
    }
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, []);

  const handleCancelDiscard = () => {
    setDiscardModalOpen(false);
    setNextLocation(null);
    setDiscardSource(null);
  };

  const handleDiscardConfirm = () => {
    // Reset state (optional)
    if (initialSnapshotRef.current) {
      const snap = JSON.parse(initialSnapshotRef.current);
      if (snap?.pricingRows) {
        setPricingRows(snap.pricingRows);
      }
    }

    // Allow next navigation
    allowNavigationRef.current = true;
    setDiscardModalOpen(false);

    // Cancel button → Platform Pricing
    if (discardSource === "cancel") {
      history.push("/platform-pricing", {
        notification: {
          type: "warning",
          recordName: "Pricing",
          action: "discarded",
          message: "Pricing Discarded",
        },
      });
      return;
    }

    // Navigation attempt → intended route
    if (discardSource === "navigate" && nextLocation) {
      history.push(nextLocation.pathname, nextLocation.state || {});
      return;
    }

    // Fallback
    history.push("/platform-pricing");
  };


  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        select_stock_open &&
        select_stock_ref.current &&
        !select_stock_ref.current.contains(e.target)
      ) {
        set_select_stock_open(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [select_stock_open]);

  useEffect(() => {
    const handler = (e) => {
      const clickedInPortal = portalRoot && portalRoot.contains(e.target);
      set_rows((prev) =>
        prev.map((row) => {
          const el = row_refs.current[row.id]?.current;
          if (!clickedInPortal && el && !el.contains(e.target)) {
            return { ...row, platform_open: false };
          }
          return row;
        }),
      );
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // incomingSelected and pricingRows (kept)
  const incomingSelected = location?.state?.bulkItems ?? null;
  console.log("incomingSelected:", JSON.stringify(incomingSelected, null, 2));
  const [selectedForBulk, setSelectedForBulk] = useState(() => {
    if (Array.isArray(incomingSelected) && incomingSelected.length)
      return incomingSelected;
    return [STOCK_OPTIONS[5], STOCK_OPTIONS[2], STOCK_OPTIONS[3]];
  });
  const [pricingRows, setPricingRows] = useState(() =>
    selectedForBulk.map((it, i) => ({
      id: i + 1,
      title: it.title,
      variant: it.sku,
      condition: it.condition,
      grading: it.status,
      basePrice: "178.00",
      erpPrices: ["1,113.00", "1,113.00", "1,113.00"],
    })),
  );
  useEffect(() => {
    setPricingRows(
      selectedForBulk.map((it, i) => ({
        id: i + 1,
        title: it.title,
        variant: it.sku,
        condition: it.condition,
        grading: it.status,
        basePrice: "178.00",
        erpPrices: ["1,113.00", "1,113.00", "1,113.00"],
      })),
    );
  }, [selectedForBulk]);

  useEffect(() => {
    if (!initialSnapshotRef.current) {
      initialSnapshotRef.current = JSON.stringify({
        pricingRows,
      });
    }
  }, [pricingRows]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        changepriceRef.current &&
        !changepriceRef.current.contains(event.target)
      ) {
        setChangepriceModalOpen(false);
      }
    }
    if (changepriceModalOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [changepriceModalOpen]);

  const handleToListing = () => {
    history.push("/listing-pricing", { activeTab: "listing", fromBulk: true });
  };
  const handleViewPricingHistory = () => {
    history.push("/listing-pricing", {
      activeTab: "price-history",
      fromBulk: true,
    });
  };
  const parsePrice = (val) => {
    if (!val) return 0;

    const cleaned = String(val)
      .replace("SGD", "")
      .replace(/,/g, "")
      .trim();

    const num = parseFloat(cleaned);

    return isNaN(num) ? 0 : num;
  };


  const handleChangepriceReasonConfirm = async () => {
    if (!changeReason || changeReason.trim() === "") {
      // setChangeReasonError("Reason is required");
      setChangeReasonError("Empty Fields");
      return;
    }

    const listingsPayload = [];

    selectedForBulk.forEach((item) => {
      const pricesPayload = [];

      (item.platforms || []).forEach((p) => {
        if (!p?.id) {
          console.error("❌ Missing platform id:", p);
          return;
        }

        const baseKey = `${item.id}_base`;
        const priceKey = `${item.id}_${p.sales_listing_mapping_id}`;

        const editedBase = editedPrices[baseKey];
        const editedOverride = editedPrices[priceKey];

        const originalBase =
          item.basePrice && item.basePrice !== "-"
            ? String(item.basePrice).replace("SGD ", "")
            : "";

        const originalOverride =
          p.erpPrice && p.erpPrice !== "-"
            ? String(p.erpPrice).replace("SGD ", "")
            : "";

        pricesPayload.push({
          sales_listing_mapping_id: p.sales_listing_mapping_id,
          sales_platform_id: p.id,
          base_price: parsePrice(editedBase ?? originalBase),
          override_price: parsePrice(editedOverride ?? originalOverride),
        });
      });

      if (pricesPayload.length > 0) {
        listingsPayload.push({
          listing_id: item.id,
          prices: pricesPayload,
        });
      }
    });

    if (listingsPayload.length === 0) {
      console.error("❌ No valid listings to save");
      return;
    }

    const payload = {
      reason: changeReason,
      listings: listingsPayload,
    };

    console.log("🚀 FINAL PAYLOAD", payload);

    try {
      const response = await apiHelper(
        "sales/pricing/bulk-pricing/",
        "POST",
        payload
      );

      if (response.ok) {
        setChangepriceModalOpen(false);
        setChangeReason("");
        allowNavigationRef.current = true
        history.push("/platform-pricing", {
          notification: {
            type: "success",
            recordName: "Pricing",
            action: "saved",
            message: "Bulk Pricing Saved Successfully",
          },
        });
      } else {
        const err = await response.json();
        console.error("Save failed", err);
      }
    } catch (e) {
      console.error("Network error", e);
    }
  };

  return (
    <div className="company-body main-company-body-inner new-look bulk-pricing-root">
      <div className="breadcrumb">
        {breadcrumbs.map((crumb, index) => {
          const isLast = index === breadcrumbs.length - 1;
          return (
            <span
              key={index}
              className={`breadcrumb-item ${crumb === "Platform Pricing" ? "clickable" : ""}`}
              onClick={() => {
                if (crumb === "Platform Pricing")
                  history.push("/platform-pricing");
              }}
              style={{
                cursor: crumb === "Platform Pricing" ? "pointer" : "default",
              }}
            >
              {crumb}
              {!isLast ? "  " : ""}
            </span>
          );
        })}
      </div>

      <div className="edit-organisation-info">
        <div className="edit-organisation-left">
          <span
            style={{
              color: "var(--Gray-900, #212529)",
              fontFamily: "Montserrat",
              fontSize: 20,
              fontStyle: "normal",
              fontWeight: 700,
              lineHeight: "normal",
            }}
          >
            Bulk Pricing
          </span>
        </div>
        <div className="discard-save-organisation-right d-flex align-items-center justify-content-end flex-wrap gap-2">

          {!editSuccess ? (
            <>

              <button
                className="dark-button"
                type="button"
                onClick={() => {
                  setDiscardSource("cancel");
                  setNextLocation(null);
                  setDiscardModalOpen(true);
                }}
              >
                Discard
              </button>
              <DiscardConfirmationModal
                isOpen={discardModalOpen}
                onCancel={handleCancelDiscard}
                onConfirm={handleDiscardConfirm}
                entityName="Bulk Pricing"
              />

              {notification && (
                <AlertNotification
                  type={notification.type}
                  recordName={notification.recordName}
                  action={notification.action}
                  message={notification.message}
                  onClose={handleNotificationClose}
                />
              )}
              {/* Work In Progress Modal */}
              <WorkInProgress
                isOpen={showWip}
                onClose={() => setShowWip(false)}
                onConfirm={() => setShowWip(false)}
              />

              <button
                className="dark-border"
                type="button"
                onClick={() => {
                  const newBaseErrors = {};
                  const newOverrideErrors = {};
                  let hasError = false;

                  document
                    .querySelectorAll(".entities-table input")
                    .forEach((input) => {
                      const val = input.value.trim();
                      const typeAttr = input.dataset.type;

                      let error = "";
                      let type = "";

                      if (!val) {
                        error = "Empty Fields";
                        type = "empty";
                      } else if (/[a-zA-Z]/.test(val)) {
                        error = "Invalid Price";
                        type = "alphabet";
                      } else if (/[^0-9.,]/.test(val)) {
                        error = "Invalid Price";
                        type = "special";
                      } else if (isNaN(Number(val.replace(/,/g, "")))) {
                        error = "Invalid Price";
                        type = "numeric";
                      }

                      if (!error) return;

                      hasError = true;

                      if (typeAttr === "base") {
                        newBaseErrors[input.dataset.id] = {
                          message: error,
                          type,
                        };
                      }

                      if (typeAttr === "override") {
                        newOverrideErrors[input.dataset.key] = {
                          message: error,
                          type,
                        };
                      }
                    });

                  setBasePriceErrors(newBaseErrors);
                  setOverrideErrors(newOverrideErrors);

                  if (hasError) {
                    setNotification({
                      type: "error",
                      recordName: "Bulk Pricing",
                      action: "saving",
                      message: "Error Saving",
                    });
                    return;
                  }

                  setNotification(null);
                  setChangepriceModalOpen(true);
                }}
              >
                Save
              </button>
            </>
          ) : (
            <button
              className="dark-button"
              type="button"
              onClick={() => setEditSuccess(false)}
            >
              Back to Edit
            </button>
          )}
        </div>
      </div>

      {/* Change Price Modal */}
      {changepriceModalOpen && (
        <>
          <div className="drawer-overlay" />
          <div
            className="entity-drawer modal-popup-cls"
            ref={changepriceRef}
            style={{
              maxWidth: 650,
              margin: "0 auto",
              background: "white",
              borderRadius: 12,
              zIndex: 1050,
              padding: 24,
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.2)",
              height: "auto",
              right: "auto",
              overflow: "inherit",
            }}
          >
            <div className="drawer-header d-flex justify-content-end align-items-center pt-0 pe-0">
              <button
                className="close-btn"
                onClick={() => {
                  setChangepriceModalOpen(false);
                  closeChangePriceModal();
                }}
              >
                <X size={20} />
              </button>
            </div>

            <h3
              style={{
                textAlign: "left",
                color: "#212529",
                fontSize: "20px",
                fontWeight: "600",
                lineHeight: "24px",
                fontFamily: "Montserrat",
                marginBottom: "5px",
              }}
            >
              Change Price
            </h3>
            <p
              style={{
                marginBottom: "0",
                textAlign: "left",
                color: "#6C757D",
                fontSize: "14px",
                fontWeight: "400",
                lineHeight: "20px",
                fontFamily: "Montserrat",
              }}
            >
              Please provide a reason for the price change. This helps maintain
              a clear record for audit and tracking purposes.
            </p>

            <div className="drawer-content">
              <div className="row gappingcls-added">
                <div className="col-12">
                  <div className="info-inner tab-panel">
                    <div className="field-block">
                      <div
                        className="field-label"
                        style={{
                          display: "flex",
                          gap: "4px",
                          alignItems: "center",
                        }}
                      >
                        Reason{" "}
                        <span className="error-color" style={{ color: "red" }}>
                          *
                        </span>
                      </div>

                      <textarea
                        className="form-control w-100 common-textarea-all"
                        rows="3"
                        name="Reason"
                        value={changeReason}
                        onChange={(e) => {
                          setChangeReason(e.target.value);

                          // clear error while typing
                          if (changeReasonError) {
                            setChangeReasonError("");
                          }
                        }}
                        style={{
                          borderColor: changeReasonError
                            ? "#DC3545"
                            : "#6c757d",
                          borderRadius: "8px",
                          resize: "none",
                          height: "120px",
                          overflowY: "auto",
                          fontFamily: '"Montserrat", sans-serif',
                          color: "#212529",
                          fontSize: "14px",
                          fontWeight: 400,
                          lineHeight: "18px",
                          boxShadow: "none",
                        }}
                      />
                      {changeReasonError && (
                        <div
                          style={{
                            color: "#DC3545",
                            fontSize: 12,
                            marginTop: 4,
                          }}
                        >
                          {changeReasonError}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="drawer-footer" style={{ paddingTop: "10px" }}>
              <button
                className="btn-cancel"
                onClick={() => {
                  setChangepriceModalOpen(false);
                  closeChangePriceModal();
                }}
              >
                {/* Discard */}
                Cancel
              </button>
              <button
                className="btn-save"
                onClick={handleChangepriceReasonConfirm}
              >
                Save
              </button>
            </div>
          </div>
        </>
      )}

      <div className="employee-tabs-container">
        {save_modal_open_2 && (
          <>
            <div className="drawer-overlay" />
            <div
              className="entity-drawer modal-popup-cls"
              ref={save_ref2}
              style={{
                maxWidth: 650,
                margin: "0 auto",
                background: "white",
                borderRadius: 12,
                zIndex: 1050,
                padding: 24,
                position: "fixed",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                boxShadow: "0px 2px 10px rgba(0,0,0,0.2)",
                height: "auto",
                right: "auto",
                overflow: "inherit",
              }}
            >
              <div className="drawer-header d-flex justify-content-end align-items-center pt-0 pe-0">
                <button
                  className="close-btn"
                  onClick={() => set_save_modal_open_2(false)}
                >
                  <X size={20} />
                </button>
              </div>
              <h3
                style={{
                  textAlign: "left",
                  color: "#212529",
                  fontSize: "20px",
                  fontWeight: "600",
                  lineHeight: "24px",
                  fontFamily: "Montserrat",
                  marginBottom: "5px",
                }}
              >
                The edit function implementation
              </h3>
              <div className="drawer-footer" style={{ paddingTop: "10px" }}>
                <button className="btn-save" onClick={handle_save_confirm_2}>
                  OK
                </button>
              </div>
            </div>
          </>
        )}

        <div className="tab-content" id="edit-spu-content">
          <div className="info-section">
            {/* If editSuccess is true, show the final success card (screenshot design). Otherwise show the edit pricing table. */}
            {editSuccess ? (
              <div className="info-card">
                <div className="info-content d-block entities-tab">
                  <div
                    className="success-visual"
                    style={{ textAlign: "center", marginBottom: 10 }}
                  >
                    <div
                      className="success-icon"
                      aria-hidden
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: 999,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "#FFEB00",
                        border: "2px solid #212529",
                      }}
                    >
                      <svg
                        width="16"
                        height="11"
                        viewBox="0 0 20 15"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M18 2L7 13L2 8"
                          stroke="black"
                          stroke-width="2.66667"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg>
                    </div>
                  </div>

                  <h2
                    style={{
                      textAlign: "center",
                      margin: 0,
                      fontFamily: "Montserrat",
                      fontSize: 20,
                      lineHeight: "24px",
                      fontWeight: 600,
                      color: "#212529",
                    }}
                  >
                    Edit Success
                  </h2>

                  <p
                    style={{
                      textAlign: "center",
                      marginTop: 10,
                      marginBottom: 20,
                      color: "#6C757D",
                    }}
                  >
                    The following prices are now reflected on the respective
                    eCommerce Platforms
                  </p>

                  <div
                    className="edit-organisation-info"
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      gap: 10,
                      paddingTop: 10,
                      paddingBottom: 10,
                      flexWrap: "wrap",
                      margin: 0,
                      boxShadow: "none",
                      borderRadius: "0",
                      backgroundColor: "transparent",
                    }}
                  >
                    <button
                      className="dark-button"
                      onClick={handleViewPricingHistory}
                    >
                      View Pricing History
                    </button>
                    <button className="dark-border" onClick={handleToListing}>
                      To Listing
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div
                className="info-card new-info-overflow-auto main-company-body-inner"
                style={{ borderRadius: 0 }}
              >
                <div className="info-content d-block entities-tab">
                  <div>
                    <div
                      className="table-wrapper"
                      style={{ position: "relative", overflowX: "auto" }}
                    >
                      <table className="entities-table tab-table w-100">
                        <thead>
                          <tr>
                            <th style={{ width: "40px" }} />
                            <th style={{ width: "220px" }}>
                              <span>Listing</span>
                            </th>
                            <th style={{ width: "300px" }}>
                              <span>Base Price</span>
                            </th>
                            <th style={{ width: "120px" }}>
                              <span>Platform</span>
                            </th>
                            <th style={{ width: "300px" }}>
                              <span>Override</span>
                            </th>
                          </tr>
                        </thead>

                        <tbody>

                          {selectedForBulk.map((item, idx) => (
                            <PriceRow
                              key={item.id ?? idx}
                              item={item}
                              rowIndex={idx}
                              editedPrices={editedPrices}
                              setEditedPrices={setEditedPrices}
                              basePriceErrors={basePriceErrors}
                              setBasePriceErrors={setBasePriceErrors}
                              overrideErrors={overrideErrors}
                              setOverrideErrors={setOverrideErrors}
                              isLastRow={selectedForBulk.length === 1}
                              onDiscard={() => {
                                setDiscardSource("cancel");
                                setNextLocation(null);
                                setDiscardModalOpen(true);
                              }}
                              onDelete={() =>
                                setSelectedForBulk((prev) =>
                                  prev.filter((_, i) => i !== idx)
                                )
                              }
                            />
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function PriceRow({
  item,
  rowIndex,
  editedPrices,
  setEditedPrices,
  setBasePriceErrors,
  basePriceErrors,
  overrideErrors,
  setOverrideErrors,
  isLastRow,
  onDiscard,
  onDelete,
}) {
  const platforms = Array.isArray(item.platforms) ? item.platforms : [];

  const hasSpecialChars = (val) => /[^0-9.,]/.test(val);
  const hasAlphabet = (val) => /[a-zA-Z]/.test(val);
  const priceInputWrapper = {
    display: "inline-flex",
    alignItems: "center",
    border: "1px solid #CED4DA",
    borderRadius: 8,
    minWidth: 270,
    height: 42,
    overflow: "hidden",
    background: "#fff",
  };

  const currencyStyle = {
    padding: "0 12px",
    fontSize: 14,
    color: "#6C757D",
    borderRight: "1px solid #CED4DA",
    height: "100%",
    display: "flex",
    alignItems: "center",
    background: "#F8F9FA",
  };

  const inputStyle = {
    border: "none",
    outline: "none",
    boxShadow: "none",
    padding: "0 12px",
    fontSize: 14,
    height: "100%",
    width: "100%",
  };
  const baseKey = `${item.id}_base`;

  return (
    <tr style={{ background: rowIndex % 2 === 1 ? "#F8F9FB" : "transparent" }}>
      {/* DELETE */}
      <td style={{ verticalAlign: "middle", textAlign: "center" }}>
        <button
          type="button"
          onClick={isLastRow ? onDiscard : onDelete}
          style={{
            background: "none",
            border: "none",
            padding: 0,
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
            <path d="M2.25 4.5H15.75M14.25 4.5V15C14.25 15.75 13.5 16.5 12.75 16.5H5.25C4.5 16.5 3.75 15.75 3.75 15V4.5M6 4.5V3C6 2.25 6.75 1.5 7.5 1.5H10.5C11.25 1.5 12 2.25 12 3V4.5M7.5 8.25V12.75M10.5 8.25V12.75" stroke="#6C757D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      </td>
      {/* STOCK */}
      <td style={{ verticalAlign: "middle" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: 8,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
            }}
          >
            <img src={item.avatar} alt="" style={{ width: 36, height: 36 }} />
          </div>

          <div>
            <div>{item.name}</div>
            <div style={{ marginTop: 4, fontSize: 10, color: "#6C757D" }}>
              {item.variant}
            </div>

            <div style={{ marginTop: 4 }}>
              <span
                style={{
                  marginRight: 6,
                  border: "1px solid #FFEB00",
                  backgroundColor: "#FFEB00",
                  color: "#212529",
                  textAlign: "center",
                  fontFamily: "Montserrat, sans-serif",
                  fontWeight: 600,
                  fontSize: 8,
                  borderRadius: 99,
                  padding: "2px 5px",
                }}
              >
                {item.condition}
              </span>
              <span
                style={{
                  marginRight: 6,
                  border: "1px solid #FFEB00",
                  backgroundColor: "#FFEB00",
                  color: "#212529",
                  textAlign: "center",
                  fontFamily: "Montserrat, sans-serif",
                  fontWeight: 600,
                  fontSize: 8,
                  borderRadius: 99,
                  padding: "2px 5px",
                }}
              >
                {item.grading}
              </span>
            </div>
          </div>
        </div>
      </td>

      {/* BASE PRICE */}
      <td style={{ verticalAlign: "middle" }}>
        <div style={{ marginRight: "-20px" }}>
          <div
            style={{
              ...priceInputWrapper,
              border: basePriceErrors[item.id]
                ? "1px solid #DC3545"
                : "1px solid #CED4DA",
            }}
          >
            <div style={currencyStyle}>SGD</div>
            <input
              data-type="base"
              data-id={item.id}
              type="text"
              value={
                editedPrices[baseKey] ??
                (item.basePrice && item.basePrice !== "-"
                  ? String(item.basePrice).replace("SGD ", "")
                  : "")
              }
              className="form-control"
              style={{
                ...inputStyle,
                border: "none",
                // color: basePriceErrors[item.id] ? "#DC3545" : "#212529",
                color: "#6C757D",
                // ["alphabet", "numeric", "empty"].includes(
                //   basePriceErrors[item.id]?.type,
                // )
                //   ? "#DC3545"
                //   : "#6C757D",
              }}
              onChange={(e) => {
                const val = e.target.value;

                setEditedPrices((prev) => ({
                  ...prev,
                  [baseKey]: val,
                }));

                setBasePriceErrors((prev) => ({
                  ...prev,
                  [item.id]: "",
                }));
              }}
              onBlur={(e) => {
                const val = e.target.value;

                let error = "";
                let type = "";

                if (!val || val.trim() === "") {
                  error = "Empty Fields";
                  type = "empty";
                } else if (hasAlphabet(val)) {
                  error = "Invalid Price";
                  type = "alphabet";
                } else if (hasSpecialChars(val)) {
                  error = "Invalid Price";
                  type = "special";
                } else if (isNaN(Number(val.replace(/,/g, "")))) {
                  error = "Invalid Price";
                  type = "numeric";
                }

                setBasePriceErrors((prev) => ({
                  ...prev,
                  [item.id]: error ? { message: error, type } : null,
                }));
              }}
            />
          </div>

          {basePriceErrors[item.id] && (
            <div
              style={{
                color: "#DC3545",
                fontSize: 12,
                marginTop: 4,
                fontWeight: 400,
              }}
            >
              {/* {basePriceErrors[item.id]} */}
              {basePriceErrors[item.id]?.message}
            </div>
          )}
        </div>
      </td>

      {/* PLATFORM */}
      <td style={{ verticalAlign: "middle" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {platforms.map((platform) => (
            <div
              key={platform.sales_listing_mapping_id}
              style={{
                height: 42,
                display: "flex",
                alignItems: "center",
              }}
            >
              <span style={{ fontSize: 12, fontWeight: 400 }}>
                {platform.name}
              </span>
            </div>
          ))}
        </div>
      </td>

      {/* Override PRICE */}
      <td style={{ verticalAlign: "middle" }}>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: 10,
          }}
        >
          {platforms.map((platform) => {
            const priceKey = `${item.id}_${platform.sales_listing_mapping_id}`;
            if (!platform) return null;

            const errorKey = `${item.id}_${platform.sales_listing_mapping_id}`; // ✅ use ID

            return (
              <div key={platform.id} style={{ width: "100%" }}> {/* ✅ use ID */}
                { }
                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    border: overrideErrors[errorKey]
                      ? "1px solid #DC3545"
                      : "1px solid #CED4DA",
                    borderRadius: 8,
                    minWidth: 290,
                    height: 42,
                    overflow: "hidden",
                    background: "#fff",
                  }}
                >
                  {/* Currency */}
                  <div
                    style={{
                      padding: "0 12px",
                      fontSize: 14,
                      color: "#6C757D",
                      background: "#F8F9FA",
                      borderRight: "1px solid #CED4DA",
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    SGD
                  </div>

                  {/* Input */}
                  <input
                    data-type="override"
                    data-listing-id={item.id}
                    data-platform-id={platform.id}
                    data-key={errorKey}
                    data-mapping-id={platform.sales_listing_mapping_id}
                    type="text"
                    value={
                      editedPrices[priceKey] ??
                      (platform.erpPrice && platform.erpPrice !== "-"
                        ? String(platform.erpPrice).replace("SGD ", "")
                        : "")
                    }
                    className="form-control"
                    style={{
                      border: "none",
                      outline: "none",
                      boxShadow: "none",
                      height: "100%",
                      padding: "0 12px",
                      fontSize: 14,
                      // color: overrideErrors[errorKey] ? "#DC3545" : "#212529",
                      color: "#6C757D",
                      // ["alphabet", "numeric", "empty"].includes(
                      //   overrideErrors[errorKey]?.type,
                      // )
                      //   ? "#DC3545"
                      //   : "#6C757D",
                    }}
                    onChange={(e) => {
                      const val = e.target.value;

                      setEditedPrices((prev) => ({
                        ...prev,
                        [priceKey]: val,
                      }));

                      setOverrideErrors((prev) => ({
                        ...prev,
                        [priceKey]: "",
                      }));
                    }}
                    onBlur={(e) => {
                      const val = e.target.value;

                      let error = "";
                      let type = "";

                      if (!val || val.trim() === "") {
                        error = "Empty Fields";
                        type = "empty";
                      } else if (hasAlphabet(val)) {
                        error = "Invalid Price";
                        type = "alphabet";
                      } else if (hasSpecialChars(val)) {
                        error = "Invalid Price";
                        type = "special";
                      } else if (isNaN(Number(val.replace(/,/g, "")))) {
                        error = "Invalid Price";
                        type = "numeric";
                      }

                      setOverrideErrors((prev) => ({
                        ...prev,
                        [errorKey]: error ? { message: error, type } : null,
                      }));
                    }}
                  />
                </div>

                {/* Error message — scoped correctly */}
                {overrideErrors[errorKey] && (
                  <div
                    style={{
                      color: "#DC3545",
                      fontSize: 12,
                      marginTop: 4,
                      marginLeft: 4,
                      fontWeight: 400,
                    }}
                  >
                    {/* {overrideErrors[errorKey]} */}
                    {overrideErrors[errorKey]?.message}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </td>
    </tr>
  );
}